import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { useGetVehicles } from "@/entities/get-vehicles/fetch";
import { usePostOffersCargos } from "@/entities/post-offers-cargos/fetch";
import type { PostOffersCargosMutationVariables } from "@/entities/post-offers-cargos/model";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { fetchOfferCargoProcess } from "@/features/offer-cargo-process/fetch";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { excludeNull } from "@/shared/lib/collection";
import { fetchFileFromUrl } from "@/shared/lib/file";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { transformLoadable } from "@/shared/lib/loadable";
import { safeParseInt } from "@/shared/lib/number";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useNavigate } from "@tanstack/react-router";
import { Result } from "neverthrow";
import { useCallback, useMemo, useState } from "react";
import type { ShipperContractCreatePageProps } from "../model";

export const useShipperContractCreatProps = (
  deliveryProposalOfferCargoIdStr: string | undefined,
): ShipperContractCreatePageProps => {
  const deliveryProposalOfferCargoId = useMemo(() => {
    if (!deliveryProposalOfferCargoIdStr) {
      return undefined;
    }
    const id = safeParseInt(deliveryProposalOfferCargoIdStr);
    if (!id) {
      return undefined;
    }
    return id;
  }, [deliveryProposalOfferCargoIdStr]);
  if (!deliveryProposalOfferCargoId) {
    throw new Error("deliveryProposalOfferCargoId is required");
  }

  // Auth
  const { session } = useAuth();

  // State
  const [previewValues, setPreviewValues] =
    useState<ShipperContractCreatePageProps["previewValues"]>(undefined);

  // Local Storage
  // 記入途中の情報をローカルストレージに保存する
  const [inProgressValues, setInProgressValues, removeInProgressValues] =
    useInProgressValuesLocalCache<
      ShipperContractCreatePageProps["defaultInputValues"]
    >("ShipperContractCreatePage");

  // Backend API
  const getDeliveryProposalOffersCargo = useGetOffersCargo(
    {
      id: deliveryProposalOfferCargoId ?? -1,
    },
    {
      enabled: !!deliveryProposalOfferCargoId,
    },
  );

  const postOffersCargos = usePostOffersCargos();

  const getVehicles = useGetVehicles(
    {
      search: {
        companyId: session.isAuthenticated ? session.user.companyId : undefined,
      },
      pagination: {
        // TODO: ページング機能を実装する際に修正
        displayLimit: 10000,
      },
    },
    {
      enabled: session.isAuthenticated,
    },
  );

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Props
  const { offerCargoProcess } = fetchOfferCargoProcess(
    getDeliveryProposalOffersCargo.data?.data.process.id,
  );

  // Actions
  const navigate = useNavigate();
  const onInputValuesUpdate = useCallback<
    ShipperContractCreatePageProps["onInputValuesUpdate"]
  >(
    (deliveryProposalOfferCargo) => {
      setInProgressValues(deliveryProposalOfferCargo);
    },
    [setInProgressValues],
  );

  const onConfirmClick = useCallback<
    ShipperContractCreatePageProps["onConfirmClick"]
  >((deliveryProposalOfferCargo) => {
    setPreviewValues(deliveryProposalOfferCargo);
  }, []);

  const onBackToInProgressClick = useCallback<
    ShipperContractCreatePageProps["onBackToInProgressClick"]
  >(() => {
    setPreviewValues(undefined);
  }, []);

  const onSubmitClick = useCallback<
    ShipperContractCreatePageProps["onSubmitClick"]
  >(async () => {
    const getDeliveryProposalOffersCargoData =
      getDeliveryProposalOffersCargo.data;
    if (!getDeliveryProposalOffersCargoData?.data) {
      return;
    }
    const destinationFilesResults = await Promise.all(
      excludeNull(
        getDeliveryProposalOffersCargoData.data.attachedDestinationFiles?.map(
          (file) => {
            if (!file.url || !file.filename) {
              return null;
            }
            return fetchFileFromUrl(file.url, file.filename);
          },
        ) ?? [],
      ),
    );
    const destinationFiles = Result.combine(destinationFilesResults);
    if (destinationFiles.isErr()) {
      setGlobalNotification({
        variant: "error",
        message:
          "申し訳ありません。契約の作成に失敗しました。ページをリロードしてもう一度お試しください。",
      });
      return;
    }

    const deliveryRequestFilesResults = await Promise.all(
      excludeNull(
        getDeliveryProposalOffersCargoData.data.attachedDeliveryRequestFiles?.map(
          (file) => {
            if (!file.url || !file.filename) {
              return null;
            }
            return fetchFileFromUrl(file.url, file.filename);
          },
        ) ?? [],
      ),
    );
    const deliveryRequestFiles = Result.combine(deliveryRequestFilesResults);
    if (deliveryRequestFiles.isErr()) {
      setGlobalNotification({
        variant: "error",
        message:
          "申し訳ありません。契約の作成に失敗しました。ページをリロードしてもう一度お試しください。",
      });
      return;
    }

    const destinations: PostOffersCargosMutationVariables["data"]["destinations"] =
      getDeliveryProposalOffersCargoData.data.destinations.map(
        (destination) => {
          if (!destination.address)
            return { ...destination, city: "", addressLine: "" };

          const parts = destination.address.split(" ");
          const city = parts.length > 1 ? parts[0] : destination.address;
          const addressLine =
            parts.length > 1 ? parts.slice(1).join(" ") : undefined;

          return {
            ...destination,
            city,
            addressLine,
          };
        },
      );

    const response = await postOffersCargos.mutateAsync({
      data: {
        ...getDeliveryProposalOffersCargoData.data,
        stage: "contract_proposal",
        processId: getDeliveryProposalOffersCargoData.data.process.id,
        sourceOfferCargoId: getDeliveryProposalOffersCargoData.data.id,
        vehicleId:
          previewValues?.vehicleId ??
          getDeliveryProposalOffersCargoData.data.vehicle?.id ??
          null,
        vehicleWeight:
          previewValues?.vehicleWeight ??
          getDeliveryProposalOffersCargoData.data.vehicleWeight,
        vehicleShape:
          previewValues?.vehicleShape ??
          getDeliveryProposalOffersCargoData.data.vehicleShape,
        vehicleEquipmentMemo:
          previewValues?.vehicleEquipmentMemo ??
          getDeliveryProposalOffersCargoData.data.vehicleEquipmentMemo,
        shippingFee:
          previewValues?.shippingFee ??
          getDeliveryProposalOffersCargoData.data.shippingFee,
        loadingOn:
          previewValues?.loadingOn ??
          getDeliveryProposalOffersCargoData.data.loadingOn,
        unloadingOn:
          previewValues?.unloadingOn ??
          getDeliveryProposalOffersCargoData.data.unloadingOn,
        memo:
          previewValues?.memo ?? getDeliveryProposalOffersCargoData.data.memo,
        destinations,
        destinationFiles: destinationFiles.value,
        deliveryRequestFiles: deliveryRequestFiles.value,
      },
    });
    if (response && isSuccessStatusCode(response.status)) {
      setGlobalNotification({
        variant: "success",
        message: "契約作成が完了しました",
        autoHide: true,
      });
      removeInProgressValues();
      // TODO: 元の荷物詳細画面に戻る (draftOfferCargoのIDを取り回す必要がある)
      navigate({
        to: "/shipper/cargos",
      });
    } else {
      setGlobalNotification({
        variant: "error",
        message: "契約作成に失敗しました",
      });
    }
  }, [
    getDeliveryProposalOffersCargo.data,
    postOffersCargos.mutateAsync,
    removeInProgressValues,
    setGlobalNotification,
    navigate,
    previewValues?.vehicleId,
    previewValues?.vehicleWeight,
    previewValues?.vehicleShape,
    previewValues?.vehicleEquipmentMemo,
    previewValues?.shippingFee,
    previewValues?.loadingOn,
    previewValues?.unloadingOn,
    previewValues?.memo,
  ]);

  return {
    deliveryProposalOfferCargoId,
    offerCargoProcess,
    defaultInputValues: inProgressValues,
    vehicles: transformLoadable(
      loadableFromUseQueryResult(getVehicles),
      (vehicles) => vehicles.data.vehicles,
    ),
    previewValues,
    onInputValuesUpdate,
    onConfirmClick,
    onBackToInProgressClick,
    onSubmitClick,
  };
};
